import { data, useActionData } from 'react-router'
import { BookDemoTabbedForm } from '#app/components/book-demo-tabbed-form.tsx'
import {
	FeatureGrid,
	MarketingButton,
	MarketingHero,
	MarketingPageShell,
	MarketingSection,
	type MarketingFeature,
} from '#app/components/marketing-page-shell.tsx'
import {
	createFaqSchema,
	powersportsMarineWebsiteSchema,
} from '#app/utils/constants.ts'
import { processSalesLeadSubmission } from '#app/utils/marketing-form.server.ts'
import { type Route } from './+types/powersports-marine-dealers.ts'

const MetaTitle = `Powersports & Marine Dealer Websites | Fast, Mobile-First Sites`
const MetaDescription = `Fast, mobile-first websites built for powersports and marine dealerships. Technical SEO, inventory photo tooling, and reliable support built for the way adventurers shop.`

const directScheduleNamespace = 'website-demo-direct'

export const handle = { chrome: 'payload' }

const features: MarketingFeature[] = [
	{
		label: '01',
		title: 'Mobile-first for research-heavy shoppers',
		description:
			'Powersports and marine buyers spend weeks comparing models before they ever visit a showroom or marina, most of it on a phone. Every page is built mobile-first so that research feels effortless.',
	},
	{
		label: '02',
		title: 'Technical SEO built for how search works now',
		description:
			'Structured data, fast pages, and content built around how buyers actually search help your inventory surface in traditional results and AI-driven overviews alike.',
	},
	{
		label: '03',
		title: 'Inventory photo tooling that saves hours',
		description:
			'Upload and organize inventory photos, automate merchandising and watermarking, and keep every unit looking sharp without manual editing.',
	},
	{
		label: '04',
		title: 'Reusable content, built once',
		description:
			'FAQs, coupons, seasonal offers, and promotional pages can be built once and reused across units, models, and locations instead of rebuilt every time.',
	},
	{
		label: '05',
		title: 'A no-code editor for menus, pages, and blogs',
		description:
			'Update pricing, hours, offers, and blog posts without waiting on a developer, so your site stays current through every season.',
	},
	{
		label: '06',
		title: 'Reliability you can hold us to',
		description:
			'Real support from real people, an uptime SLA, and a platform built specifically for dealers, not a generic website builder retrofitted for the industry.',
	},
]

const faqs = [
	{
		question:
			'Do you build websites specifically for powersports and marine dealers?',
		answer:
			'Yes. DealerProEdge websites for powersports and marine dealers are built around how those shoppers research and buy, with inventory tooling, mobile-first design, and SEO built for the category rather than a generic dealer template.',
	},
	{
		question: 'Why does mobile page speed matter so much for my dealership?',
		answer:
			'Most powersports and marine shoppers do their research on a phone. A slow-loading page loses that shopper to a faster competitor before they ever see your inventory, so speed is treated as a core requirement, not an afterthought.',
	},
	{
		question: 'Can I manage my own inventory photos and content?',
		answer:
			'Yes. The platform includes tools to upload and organize inventory photos, automate watermarking and merchandising, and edit pages, menus, and blog posts without developer help.',
	},
	{
		question: 'What kind of support do I get after launch?',
		answer:
			'You get real people backed by an uptime SLA, not a ticket queue. Support and reliability are treated as part of the product, not an add-on.',
	},
]

const faqSchema = createFaqSchema(faqs)

export const meta: Route.MetaFunction = ({ matches }) => {
	const rootData = matches[0]?.data as
		| { requestInfo?: { origin: string; path: string } }
		| undefined
	const origin = rootData?.requestInfo?.origin ?? 'https://dealerproedge.com'
	const path =
		rootData?.requestInfo?.path ?? '/industries/powersports-marine-dealers'
	const canonicalUrl = `${origin}${path}`

	return [
		{ title: MetaTitle },
		{ name: 'description', content: MetaDescription },
		{ name: 'twitter:title', content: MetaTitle },
		{ name: 'twitter:description', content: MetaDescription },
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ property: 'og:title', content: MetaTitle },
		{ property: 'og:description', content: MetaDescription },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: canonicalUrl },
		{ tagName: 'link', rel: 'canonical', href: canonicalUrl },
		{ 'script:ld+json': powersportsMarineWebsiteSchema },
		{ 'script:ld+json': faqSchema },
	]
}

export async function action(args: Route.ActionArgs) {
	const formData = await args.request.formData()
	const result = await processSalesLeadSubmission(
		formData,
		'website-demo',
		args,
	)

	if (!result.success) {
		return data(
			{ result: result.submission },
			{
				status: result.status,
				headers: 'headers' in result ? result.headers : undefined,
			},
		)
	}

	return data({ success: true })
}

export default function PowersportsMarineDealersPage() {
	const actionData = useActionData<typeof action>()
	const didSubmit = actionData && 'success' in actionData && actionData.success
	const lastResult =
		actionData && 'result' in actionData ? actionData.result : undefined

	return (
		<MarketingPageShell>
			<MarketingHero
				eyebrow="Powersports & Marine Dealers"
				title="Websites built for the thrill of the ride and the open water."
				subtitle="Adventurers research online long before they walk into a showroom or marina, most of it on a phone. DealerProEdge gives powersports and marine dealers a fast, mobile-first website with the SEO, content tools, and support built for how this industry actually sells."
				stats={[
					{ value: '70%+', label: 'Buyers research on mobile first' },
					{ value: '3s', label: 'Load time before shoppers start leaving' },
					{ value: '24/7', label: 'Real support, not a ticket queue' },
				]}
				actions={
					<>
						<MarketingButton to="#getStarted">Schedule My Demo</MarketingButton>
						<MarketingButton to="/car-dealer-websites" variant="light">
							See the Platform
						</MarketingButton>
					</>
				}
			/>

			<MarketingSection
				eyebrow="The reality"
				title="Inventory issues and poor support have plagued powersports and marine websites."
			>
				<div className="text-black/66 max-w-4xl text-xl leading-9">
					<p>
						Too many powersports and marine websites are slow, hard to update,
						and backed by support teams that treat every issue like a ticket
						instead of a priority. Meanwhile, buyers are doing more of their
						research on mobile, comparing models and prices before they ever
						reach out. A platform built specifically for this industry closes
						that gap instead of widening it.
					</p>
				</div>
			</MarketingSection>

			<MarketingSection
				eyebrow="Built for the category"
				title="Everything your dealership needs to compete online."
			>
				<FeatureGrid features={features} />
			</MarketingSection>

			<MarketingSection
				eyebrow="Frequently asked questions"
				title="What dealers ask before switching."
			>
				<div className="grid grid-cols-1 border-l border-t border-black lg:grid-cols-2">
					{faqs.map((faq) => (
						<div
							className="border-b border-r border-black bg-[#fffffa]/60 p-7"
							key={faq.question}
						>
							<h3 className="text-xl font-medium leading-snug">
								{faq.question}
							</h3>
							<p className="text-black/64 mt-4 text-base leading-7">
								{faq.answer}
							</p>
						</div>
					))}
				</div>
			</MarketingSection>

			<MarketingSection id="getStarted" className="scroll-mt-24">
				<div className="mx-auto max-w-[620px]">
					<div className="mb-8 text-center">
						<h2 className="text-4xl font-medium leading-tight text-black md:text-5xl">
							Schedule a Demo
						</h2>
						<p className="text-black/58 mx-auto mt-4 max-w-[520px] text-base leading-7 md:text-lg">
							Submit the form below or schedule a time directly on our calendar.
						</p>
					</div>
					<BookDemoTabbedForm
						isSubmitted={Boolean(didSubmit)}
						lastResult={lastResult}
						scheduleNamespace={directScheduleNamespace}
						successDescription="Your website demo request went to the DealerProEdge team. We will review your current site and follow up shortly."
						usePairedFieldRows
					/>
				</div>
			</MarketingSection>
		</MarketingPageShell>
	)
}
